import { Component, computed, DestroyRef, inject, Signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Balance } from 'src/app/models/balance.model';
import { Payout, PayoutStatus } from 'src/app/models/payout.model';
import { AuthService } from 'src/app/services/auth.service';
import { EventsStoreService } from 'src/app/services/stores/events.store.service';
import { EventCountersStoreService } from 'src/app/services/stores/eventCounters.store.service';
import { PayoutsStoreService } from 'src/app/services/stores/payouts.store.service';
import {
  PayoutRequestDialogComponent,
  PayoutRequestDialogData,
} from './components/payout-request-dialog/payout-request-dialog.component';

@Component({
  selector: 'app-user',
  templateUrl: './user.component.html',
  styleUrl: './user.component.scss',
  standalone: false,
})
export class UserComponent {
  private _destroyRef = inject(DestroyRef);
  private _eventStoreService = inject(EventsStoreService);
  private _eventCounterStoreService = inject(EventCountersStoreService);
  private _payoutsStoreService = inject(PayoutsStoreService);
  private _dialog = inject(MatDialog);

  protected authService = inject(AuthService);

  protected totalBalanceAmount: Signal<number> = computed(() => {
    const user = this.authService.user();
    const totalNetRevenue =
      this._eventCounterStoreService
        .eventCounters()
        ?.reduce((acc, counter) => acc + (counter.netRevenue || 0), 0) || 0;

    const payouts = this._payoutsStoreService.payouts() || [];
    const totalPayoutsSent = payouts
      .filter(
        (payout) =>
          payout.status === PayoutStatus.REQUESTED ||
          payout.status === PayoutStatus.SENT_FOR_PAYMENT ||
          payout.status === PayoutStatus.PAID,
      )
      .reduce((acc, payout) => acc + (payout.amount || 0), 0);

    return Math.max(0, totalNetRevenue - totalPayoutsSent);
  });

  protected balanceTableData: Signal<Balance[]> = computed(() => {
    const events = this._eventStoreService.events() || [];
    const counters = this._eventCounterStoreService.eventCounters() || [];
    return counters.map((counter) => {
      const event = events.find((e) => e.id === counter.id);
      return {
        id: counter.id,
        eventName: event ? event.name : 'Unknown Event',
        shoutoutCount: counter.shoutoutCount,
        grossRevenue: counter.grossRevenue,
        netRevenue: counter.netRevenue,
      } as Balance;
    });
  });

  protected payouts: Signal<Payout[] | undefined> =
    this._payoutsStoreService.payouts;

  protected isAdmin: Signal<boolean> = computed(
    () => this.authService.user()?.roles?.admin === true,
  );

  constructor() {
    // Load payouts based on user role
    const user = this.authService.user();
    if (user) {
      if (user.roles?.admin) {
        this._payoutsStoreService.getAllPayouts();
      } else if (user.uid) {
        this._payoutsStoreService.getUserPayouts(user.uid);
      }
    }
  }

  protected openPayoutRequestDialog(): void {
    const user = this.authService.user();
    if (!user || !user.uid || !user.email) return;

    const dialogData: PayoutRequestDialogData = {
      userId: user.uid,
      userEmail: user.email,
      currentBalance: this.totalBalanceAmount(),
    };

    const dialogRef = this._dialog.open(PayoutRequestDialogComponent, {
      width: '500px',
      data: dialogData,
      disableClose: true,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((result) => {
        if (result) {
          // Refresh payouts after successful submission
          if (user.roles?.admin) {
            this._payoutsStoreService.getAllPayouts();
          } else if (user.uid) {
            this._payoutsStoreService.getUserPayouts(user.uid);
          }
        }
      });
  }
}
